import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, Calendar, Instagram } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { BookingSummaryCard } from "@/components/BookingSummaryCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { SITE } from "@/data/site";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ThankYouPage() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const reason = params.get("reason") || "enquiry"; // 'enquiry' | 'booking' | 'registration'
  const orderId = params.get("order_id");
  const provider = params.get("provider"); // 'paypal' (Stripe lands on /payment/success)

  const [status, setStatus] = useState(null);

  usePageMeta(
    "Thank you · USDD Academy",
    "Thanks for getting in touch with USDD Academy — we'll be in touch within 24 hours."
  );

  useEffect(() => {
    trackEvent("thank_you_view", { reason, provider });
  }, [reason, provider]);

  useEffect(() => {
    if (!orderId || provider !== "paypal") return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/checkout/paypal/status/${encodeURIComponent(orderId)}`);
        if (!cancelled) setStatus(r.data);
      } catch {
        /* swallow — summary just won't render */
      }
    })();
    return () => { cancelled = true; };
  }, [orderId, provider]);

  const COPY = {
    enquiry: { heading: "Message received.", sub: "We'll come back to you within 24 hours." },
    booking: { heading: "Booking confirmed.", sub: "Check your email for your confirmation." },
    registration: { heading: "Registration received.", sub: "We'll reach out to confirm next steps." },
  }[reason] || { heading: "Thank you.", sub: "We'll be in touch shortly." };

  return (
    <>
      <Header />
      <main>
        <section
          data-testid="thank-you-section"
          className="relative min-h-[100svh] flex items-center overflow-hidden bg-black pt-24"
        >
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(36,0,70,0.7),transparent_60%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(90,24,154,0.45),transparent_55%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,218,31,0.08),transparent_55%)]" />
            <div className="absolute inset-0 noise opacity-60 pointer-events-none" />
          </div>

          <div className="relative z-10 max-w-4xl mx-auto px-6 sm:px-8 py-20 w-full text-center">
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-brand-yellow/15 border border-brand-yellow/40 mb-8 glow-yellow"
            >
              <CheckCircle2 size={42} className="text-brand-yellow" />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="font-display uppercase text-5xl sm:text-7xl lg:text-8xl text-white tracking-wide leading-[0.9]"
            >
              {COPY.heading.split(".")[0]}
              <span className="text-brand-yellow text-glow-yellow">.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-6 max-w-xl mx-auto text-base sm:text-lg text-zinc-300/90 leading-relaxed"
            >
              {COPY.sub} While you're here — explore our timetable, follow us on Instagram or read what's new on the blog.
            </motion.p>

            {status && <BookingSummaryCard status={status} />}

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.45 }}
              className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4"
            >
              <Link
                to="/schedule"
                data-testid="thank-you-schedule-btn"
                className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-brand-yellow text-black font-black uppercase text-sm tracking-wider hover:bg-brand-yellowHover hover:scale-[1.03] transition-all glow-yellow"
              >
                <Calendar size={16} /> Book a class
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/"
                data-testid="thank-you-home-btn"
                className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-white/20 text-white font-bold uppercase text-xs tracking-[0.2em] hover:border-brand-yellow hover:text-brand-yellow transition-all"
              >
                Back to home
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.7 }}
              className="mt-16 pt-10 border-t border-white/10 flex flex-wrap items-center justify-center gap-4"
            >
              <a
                href={SITE.socials.instagram_main.url}
                target="_blank"
                rel="noreferrer"
                data-testid="thank-you-instagram-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-zinc-200 text-sm font-bold hover:border-brand-yellow hover:text-brand-yellow transition-all"
              >
                <Instagram size={14} /> {SITE.socials.instagram_main.handle}
              </a>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
